import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
  ArrowRight,
  FileText,
  Image as ImageIcon,
  Loader2,
  MessageSquare,
  X,
} from "lucide-react";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import {
  decisions as decisionsApi,
  entityAttachments as attachmentsApi,
  signals as signalsApi,
  type ApiEntity,
  type ApiSignal,
} from "@/lib/api";
import { useAuthenticatedImage } from "@/lib/authenticated-image";
import { colorForEntity } from "@/lib/entity-color";

/**
 * EntityQuickCard — the floating card that pops next to a map marker.
 *
 * Design mirrors the base-operations reference the operator sent: title +
 * type row, thumbnail from the primary attachment, latest signal excerpt,
 * and a short action rail so the operator can act without leaving the
 * map. Full-fidelity edit / relationship management still lives in the
 * entity slide-over, reachable via the "Details" action.
 *
 * Positioned in screen space by the parent (which projects the marker's
 * lng/lat to pixels via MapLibre) — that lets it follow the marker as
 * the user pans/zooms without re-rendering the map layer stack.
 */
export function EntityQuickCard({
  workspaceId,
  entity,
  entitiesAtCoord,
  screenX,
  screenY,
  onClose,
}: {
  workspaceId: string;
  entity: ApiEntity;
  /** All entities sharing this marker (for clustered coords). */
  entitiesAtCoord: ApiEntity[];
  screenX: number;
  screenY: number;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [alertOk, setAlertOk] = useState(false);

  // Clamp index when the underlying cluster shrinks (e.g. entity deleted).
  const clampedIndex = Math.min(selectedIndex, entitiesAtCoord.length - 1);
  const current = entitiesAtCoord[clampedIndex] ?? entity;

  const attachments = useQuery({
    queryKey: ["entity-attachments", workspaceId, current.id],
    queryFn: () => attachmentsApi.list(workspaceId, current.id),
    enabled: Boolean(workspaceId && current.id),
  });
  const primary = useMemo(() => {
    const list = attachments.data ?? [];
    return list.find((a) => a.is_primary) ?? list[0] ?? null;
  }, [attachments.data]);

  // Signals for this entity — filtered from the workspace-level list so we
  // don't fire an extra network call. Cache hits are the common case.
  const signals = useQuery({
    queryKey: ["signals", workspaceId],
    queryFn: () => signalsApi.list(workspaceId),
    enabled: Boolean(workspaceId),
  });
  const relatedSignals = useMemo<ApiSignal[]>(() => {
    return (signals.data ?? [])
      .filter((s) => s.related_entity_ids.includes(current.id))
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
      .slice(0, 3);
  }, [signals.data, current.id]);

  const createDecision = useMutation({
    mutationFn: async () => {
      const attrs = current.attributes ?? {};
      const summaryBits: string[] = [];
      for (const [k, v] of Object.entries(attrs)) {
        if (v == null || v === "") continue;
        const s = String(v);
        if (s.length > 200) continue;
        summaryBits.push(`- ${k}: ${s}`);
        if (summaryBits.length >= 6) break;
      }
      const title = `Risk assessment: ${current.name}`;
      const decision_input = [
        `Assess the operational risk associated with **${current.name}** (${current.type}).`,
        "",
        summaryBits.length ? "Known attributes:" : null,
        summaryBits.length ? summaryBits.join("\n") : null,
        relatedSignals.length
          ? `Recent signals (${relatedSignals.length}): ${relatedSignals
              .map((s) => s.type)
              .join(", ")}.`
          : null,
        "",
        "Return a risk score (low/medium/high), likely scenarios, and 2–3 concrete mitigations.",
      ]
        .filter(Boolean)
        .join("\n");
      return decisionsApi.create(workspaceId, { title, decision_input });
    },
    onSuccess: (decision) => {
      queryClient.invalidateQueries({ queryKey: ["decisions", workspaceId] });
      onClose();
      navigate({
        to: "/$workspace/decisions/$decisionId",
        params: { workspace: workspaceId, decisionId: decision.id },
      });
    },
  });

  // Dismiss on Escape and on outside click. We keep click-outside scoped to
  // pointerdown (rather than click) so it fires before the map's own click
  // handler tries to re-pin a different marker — otherwise a stray click on
  // the map would close-then-reopen the card in the same tick.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    const onDown = (e: MouseEvent) => {
      if (!cardRef.current) return;
      if (cardRef.current.contains(e.target as Node)) return;
      onClose();
    };
    window.addEventListener("keydown", onKey);
    // Delay outside-click subscription by a tick so the click that OPENED
    // the card doesn't immediately close it.
    const t = window.setTimeout(() => window.addEventListener("mousedown", onDown), 0);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.clearTimeout(t);
      window.removeEventListener("mousedown", onDown);
    };
  }, [onClose]);

  const color = colorForEntity(current);
  const CARD_W = 300;

  // Position is computed in a layout effect (before paint) so the very first
  // frame is placed correctly. The naive approach — reading
  // `cardRef.current.parentElement` during render — sees `null` on the first
  // render and falls back to `window.innerWidth`, which is wrong whenever the
  // map container is narrower than the window (it always is: there's a
  // sidebar). That mis-clamps the first card and only self-corrects on the
  // next pan/zoom. Here we measure the real container + the card's own
  // rendered height, then clamp, then commit — no wrong first frame.
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  useLayoutEffect(() => {
    const card = cardRef.current;
    const container = card?.parentElement;
    if (!card || !container) return;
    const cw = container.clientWidth;
    const ch = container.clientHeight;
    const cardH = card.offsetHeight || 320;
    // Preferred anchor: to the right of the marker; flip left if we'd spill.
    let left = screenX + 16;
    if (left + CARD_W > cw - 8) left = screenX - CARD_W - 16;
    if (left < 8) left = 8;
    let top = screenY - 40;
    if (top + cardH > ch - 8) top = ch - cardH - 8;
    if (top < 8) top = 8;
    setPos({ left, top });
    // Re-run when the marker moves (screenX/Y change on pan/zoom), the cluster
    // selection changes (card height differs), or attachments/signals load
    // (also changes height).
  }, [screenX, screenY, clampedIndex, primary, relatedSignals.length]);

  return (
    <div
      ref={cardRef}
      className="absolute z-30 rounded-md border border-border bg-surface/95 backdrop-blur shadow-xl text-[12px] flex flex-col"
      style={{
        left: `${pos?.left ?? screenX + 16}px`,
        top: `${pos?.top ?? screenY - 40}px`,
        width: `${CARD_W}px`,
        // Hide the very first pre-measurement frame to avoid a flash at the
        // fallback position, then reveal once positioned.
        visibility: pos ? "visible" : "hidden",
      }}
    >
      {/* Header — name, type badge, close */}
      <div className="flex items-start gap-2 px-3 pt-2.5 pb-2 border-b border-border">
        <span
          className="mt-1 h-2 w-2 rounded-full shrink-0"
          style={{ background: color }}
        />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-foreground truncate leading-tight">
            {current.name}
          </div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">
            {current.type}
          </div>
        </div>
        <button
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground -mr-1 -mt-1 p-1"
          title="Close (Esc)"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Cluster picker — only when multiple entities share this coord. */}
      {entitiesAtCoord.length > 1 && (
        <div className="px-3 pt-2 pb-1 border-b border-border/60 flex items-center gap-1 overflow-x-auto scrollbar-thin">
          {entitiesAtCoord.map((e, i) => {
            const active = i === clampedIndex;
            return (
              <button
                key={e.id}
                onClick={() => setSelectedIndex(i)}
                className={`text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap ${
                  active
                    ? "bg-primary/20 text-primary"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                title={e.name}
              >
                {e.name.length > 18 ? `${e.name.slice(0, 17)}…` : e.name}
              </button>
            );
          })}
        </div>
      )}

      {/* Thumbnail — only if the entity has an attachment. */}
      {primary?.mime_type?.startsWith("image/") ? (
        <ThumbnailImage url={primary.url} alt={current.name} />
      ) : primary ? (
        <div className="mx-3 mt-2 rounded border border-border bg-muted/30 aspect-video flex items-center justify-center text-muted-foreground gap-1.5">
          <FileText className="h-4 w-4" />
          <span className="text-[10px] font-mono truncate max-w-[220px]">
            {primary.filename}
          </span>
        </div>
      ) : (
        <div className="mx-3 mt-2 rounded border border-dashed border-border/70 bg-muted/10 aspect-video flex flex-col items-center justify-center text-muted-foreground gap-1">
          <ImageIcon className="h-4 w-4 opacity-50" />
          <span className="text-[10px]">No media yet</span>
        </div>
      )}

      {/* Recent signals — up to 3, latest first. */}
      <div className="px-3 py-2 border-t border-border/60 mt-2">
        <div className="label-eyebrow !mb-1.5">
          Recent signals ({relatedSignals.length})
        </div>
        {relatedSignals.length === 0 ? (
          <div className="text-[11px] text-muted-foreground italic">
            None yet.
          </div>
        ) : (
          <div className="space-y-1">
            {relatedSignals.map((s) => (
              <div key={s.id} className="text-[11px] flex items-baseline gap-2">
                <span className="text-foreground/90 truncate flex-1">
                  {shortTitle(s)}
                </span>
                <span className="text-[9px] font-mono text-muted-foreground shrink-0">
                  {fmtShort(s.timestamp)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Action rail */}
      <div className="border-t border-border p-2 grid grid-cols-2 gap-1.5">
        <button
          onClick={() => {
            onClose();
            navigate({
              to: ".",
              search: (prev: Record<string, unknown>) => ({ ...prev, entity: current.id }),
            });
          }}
          className="col-span-2 flex items-center justify-center gap-1.5 text-[11px] px-2 py-1.5 rounded bg-primary/15 text-primary hover:bg-primary/25 font-medium"
        >
          <span>Details</span>
          <ArrowRight className="h-3 w-3" />
        </button>
        <button
          onClick={() => createDecision.mutate()}
          disabled={createDecision.isPending}
          className="flex items-center justify-center gap-1.5 text-[11px] px-2 py-1.5 rounded bg-surface-elevated hover:bg-surface-elevated/70 text-foreground/85 disabled:opacity-50"
          title="Create a decision seeded with this entity as context"
        >
          {createDecision.isPending ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <FileText className="h-3 w-3" />
          )}
          <span>Risk report</span>
        </button>
        <button
          onClick={() => {
            // Alerts don't have a dedicated backend yet — we surface a
            // toast-style acknowledgment so the operator sees the button
            // did *something*, and log to the browser console so a bridge
            // integration can pick it up locally in dev.
            console.info("[dravian] send-alert requested", {
              entityId: current.id,
              entityName: current.name,
              workspaceId,
            });
            setAlertOk(true);
            window.setTimeout(() => setAlertOk(false), 1600);
          }}
          className={`flex items-center justify-center gap-1.5 text-[11px] px-2 py-1.5 rounded ${
            alertOk
              ? "bg-risk-high/20 text-risk-high"
              : "bg-surface-elevated hover:bg-surface-elevated/70 text-foreground/85"
          }`}
          title="Log an alert for this entity"
        >
          <MessageSquare className="h-3 w-3" />
          <span>{alertOk ? "Logged" : "Send alert"}</span>
        </button>
      </div>
    </div>
  );
}

function ThumbnailImage({ url, alt }: { url: string; alt: string }) {
  const { src } = useAuthenticatedImage(url);
  if (!src) {
    return (
      <div className="mx-3 mt-2 rounded border border-border bg-muted/30 aspect-video flex items-center justify-center">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }
  return (
    <div className="mx-3 mt-2 rounded border border-border bg-black/40 aspect-video overflow-hidden">
      <img src={src} alt={alt} className="w-full h-full object-cover" />
    </div>
  );
}

function shortTitle(s: ApiSignal): string {
  const attrs = s.attributes ?? {};
  return (
    (attrs.title as string) ||
    (attrs.headline as string) ||
    (attrs.article_title as string) ||
    (attrs.description as string) ||
    s.type
  );
}

function fmtShort(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const sameYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "2-digit" }),
  });
}
